import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import { pluralize } from '@i18n/localizeHelper'
import { useTranslations } from '@i18n/useLanguage'
import { DataCard } from './DataCard'
import { useCallback, useMemo, useState } from 'react'
import { Modal } from '@app/components/blocks/modal/Modal'
import { AvatarStack } from '@app/components/blocks/avatarStack/AvatarStack'
import { Button } from '@app/components/core/button/Button'
import { faSend } from '@fortawesome/pro-regular-svg-icons'
import { useAuthState } from '@based/sdk'
import { INITIATIVE_USER_ROLES } from '@schema/initiative'
import { isUserAdmin } from '@schema/utils'
import { ViewInitiativeModel } from 'functions/app/initiative/view'
import { fullName } from '@app/util/helpers'
import { useMessageBox } from '@app/util/hooks/useMessageBox'
import { UserList } from '@app/components/blocks/userList/UserList'
import { Tooltip } from '@app/components/core/tooltip/Tooltip'

export const RightColumn = ({
  initiative,
}: {
  initiative: ViewInitiativeModel
}) => {
  const translations = useTranslations().app.initiativePage
  const commonTranslations = useTranslations().app.common
  const { showMessageBox } = useMessageBox()
  const [usersModal, setUsersModal] = useState<'off' | 'members' | 'admins'>(
    'off',
  )
  const members = initiative.users.filter(
    (u) => u.$role === INITIATIVE_USER_ROLES.member,
  )
  const auth = useAuthState()
  const canMessageAdmin =
    auth.userId && initiative.users.some((u) => u.id === Number(auth.userId))
  const canMessageAll =
    auth.userId &&
    initiative.users.some((u) => u.id === Number(auth.userId) && isUserAdmin(u))

  const { admins, messageBoxRecipients } = useMemo(() => {
    const adminsList = initiative.users.filter((u) => isUserAdmin(u))
    const recipients = canMessageAll ? initiative.users : adminsList

    return {
      admins: adminsList,
      messageBoxRecipients: recipients,
    }
  }, [initiative.users, canMessageAll])

  const mappedRecipients = useMemo(() => {
    return messageBoxRecipients.map((r) => ({
      id: r.id,
      name: fullName(r),
      avatar: r.avatar,
      role: r.$role,
    }))
  }, [messageBoxRecipients])

  const onMessageClick = useCallback(
    (userId: number) => {
      showMessageBox(mappedRecipients, initiative.id, userId)
      setUsersModal('off')
    },
    [mappedRecipients, initiative.id, showMessageBox, setUsersModal],
  )

  return (
    <Column
      gap="2.5rem"
      alignItems="stretch"
      style={{ flex: '1 1 23rem', minWidth: '23rem' }}
    >
      <DataCard initiative={initiative} />
      {admins.length > 0 && (
        <Column>
          <Title.H4>{pluralize(translations.admin, admins.length)}</Title.H4>
          <AvatarStack
            size={'medium'}
            avatars={admins}
            onClick={() => setUsersModal('admins')}
          />
        </Column>
      )}
      {members.length > 0 && (
        <Column>
          <Tooltip content={translations.membersTooltip}>
            <Title.H4>{pluralize(translations.member, members.length)}</Title.H4>
          </Tooltip>
          <AvatarStack
            size={'medium'}
            avatars={members}
            onClick={() => setUsersModal('members')}
          />
        </Column>
      )}
      <Modal
        title={translations.admin.plural}
        isOpen={usersModal === 'admins'}
        onClose={() => setUsersModal('off')}
      >
        <UserList
          gap="1.5rem"
          users={admins.map((u) => ({
            id: u.id,
            name: fullName(u),
            avatar: u.avatar,
            handle: u.handle,
            action: canMessageAdmin && (
              <Button
                variant="secondary"
                size="small"
                icon={faSend}
                label={commonTranslations.sendMessage}
                iconPosition="left"
                onClick={() => onMessageClick(u.id)}
              />
            ),
          }))}
          style={{
            minWidth: '30rem',
          }}
        />
      </Modal>
      <Modal
        title={translations.member.plural}
        isOpen={usersModal === 'members'}
        onClose={() => setUsersModal('off')}
      >
        <UserList
          gap="1.5rem"
          users={members.map((u) => ({
            id: u.id,
            name: fullName(u),
            avatar: u.avatar,
            handle: u.handle,
            action: canMessageAll && (
              <Button
                variant="secondary"
                size="small"
                icon={faSend}
                label={commonTranslations.sendMessage}
                iconPosition="left"
                onClick={() => onMessageClick(u.id)}
              />
            ),
          }))}
          style={{
            minWidth: '30rem',
          }}
        />
      </Modal>
    </Column>
  )
}
